import { computed, inject, InjectionToken, Provider, Signal, Type } from '@angular/core';
import { DamageReportStep } from './damage-report-step';
import { ComponentProps } from '@co-3/ctrl-ui/common';
import { ContractsService } from 'app/customer-portal/contracts.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProductsService } from 'app/products/products.service';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { FileWithMeta, WithUploadStatus } from './file-with-meta';
import { Contract } from '@models/contract';

export type DamageReportStepGuardFn = (form: DamageReportForm) => boolean;

export type FileFormGroup = {
    [Key in keyof WithUploadStatus<FileWithMeta>]: FormControl<WithUploadStatus<FileWithMeta>[Key]>;
};

export type DamageReportForm = FormGroup<{
    report: FormGroup<{
        damageDate: FormControl<string>;
        location: FormControl<string>;
        eventDescription: FormControl<string>;
        damageDescription: FormControl<string>;
        notes: FormControl<string>;
    }>;
    files: FormArray<FormGroup<FileFormGroup>>;
}>;

export interface DamageReportStepConfig<T extends DamageReportStep> {
    slug: string;
    component: Type<T>;
    config?: ComponentProps<T>;
    guard?: DamageReportStepGuardFn;
}

export const damageReportStep = <T extends DamageReportStep>(
    slug: string,
    component: Type<T>,
    config?: ComponentProps<T>,
    guard?: DamageReportStepGuardFn,
): DamageReportStepConfig<T> => ({ slug, component, config, guard });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DEFAULT_DAMAGE_REPORT_STEPS = new InjectionToken<DamageReportStepConfig<any>[]>(
    'defaultDamageReportStepToken',
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const provideDefaultDamageReportSteps = (steps: DamageReportStepConfig<any>[]): Provider[] =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    steps.map((stepConfig: DamageReportStepConfig<any>) => ({
        provide: DEFAULT_DAMAGE_REPORT_STEPS,
        useValue: stepConfig,
        multi: true,
    }));

export const getStepsForCurrentProduct = (
    contractOrContractId: Signal<Contract | number | null>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Signal<DamageReportStepConfig<any>[]> => {
    const contracts = toSignal(inject(ContractsService).contracts$);
    const productsService = inject(ProductsService);
    const defaultSteps = inject(DEFAULT_DAMAGE_REPORT_STEPS);

    return computed(() => {
        let contract = contractOrContractId();
        if (typeof contract === 'number' || contract === null) {
            contract = contracts().find((c: Contract) => c.id === contractOrContractId());
        }
        if (contract) {
            return (
                productsService.getCanonicalByInsuranceType(contract.insuranceType)?.portalConfig?.damageReportSteps ??
                defaultSteps
            );
        }
        return defaultSteps;
    });
};
